import {
    CallExpressionShape,
    Record as SdkRecord,
    TemplateExpressionShape,
    type ObjectShape,
    type Shape,
    type Source,
} from '@servicenow/sdk-build-core'
import type { ArrowFunctionShape } from '@servicenow/sdk-build-plugins'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import type { MapperValue } from '../transforms/playbook-inputs-mapper-transform'
import { DATAPILL_CALLEE } from '../constants/playbook-plugin-constants'

/**
 * Given a shape that is (or resolves to) a factory call like `SubflowDefinition({...})`,
 * returns the first argument as an ObjectShape — i.e. the config passed to the factory.
 * Resolves identifiers and property access chains before checking for call expressions.
 *
 * When the shape has already been processed into a Record by another plugin (e.g. SubflowPlugin),
 * falls back to the Record's original source to recover the pre-processed CallExpressionShape.
 */
export function getFactoryCallConfig(shape: Shape): ReturnType<Shape['ifObject']> {
    const resolved = shape.isResolvable() ? shape.asResolvable().resolve() : shape

    // Direct call expression (e.g. ActivityType({...}) not processed by another plugin)
    const directCE = resolved.if(CallExpressionShape)
    if (directCE) {
        return directCE.getArgument(0)?.ifObject()
    }

    // Backing flows/actions are processed by their respective plugins into Records.
    // Recover the original CallExpressionShape from the Record's source.
    if (resolved instanceof SdkRecord) {
        const source = resolved.getSource()
        if (source instanceof CallExpressionShape) {
            return source.getArgument(0)?.ifObject()
        }
    }

    return undefined
}

/**
 * Returns true if the shape is (or contains) a `wfa.playbook.dataPill(...)` call
 * or a template expression with one inside.
 *
 * Inspects the original (pre-resolution) shape so there is no string-pattern
 * matching on resolved pill strings.
 */
export function containsDataPill(shape: Shape): boolean {
    if (shape instanceof CallExpressionShape && shape.getCallee() === DATAPILL_CALLEE) {
        return true
    }
    if (shape instanceof TemplateExpressionShape) {
        return shape.getSpans().some((span) => {
            const expr = span.getExpression()
            return expr instanceof CallExpressionShape && expr.getCallee() === DATAPILL_CALLEE
        })
    }
    return false
}

/**
 * Shape class for Lane instances within a PlaybookDefinition body.
 * Handles the pattern: wfa.playbook.lane(config)
 *
 * @example
 * wfa.playbook.lane({ $id: Now.ID['lane_1'], label: 'Main Lane', order: 1 })
 */
export class LaneShape extends CallExpressionShape {
    private config: ObjectShape
    private activities: ArrowFunctionShape | undefined
    private readonly nowIdKeyToGuid: Map<string, string> | undefined
    private readonly playbookBodyParamsName: string | undefined

    constructor({
        source,
        config,
        activities,
        nowIdKeyToGuid,
        playbookBodyParamsName,
    }: {
        source: Source
        config: ObjectShape
        activities?: ArrowFunctionShape | undefined
        nowIdKeyToGuid?: Map<string, string>
        /** Name of the lanes-level arrow function parameter representing PlaybookBodyParams */
        playbookBodyParamsName?: string | undefined
    }) {
        super({ source, callee: 'LaneShape', args: [config] })
        this.config = config
        this.activities = activities
        this.nowIdKeyToGuid = nowIdKeyToGuid
        this.playbookBodyParamsName = playbookBodyParamsName
    }

    getConfig(): ObjectShape {
        return this.config
    }

    getActivities(): ArrowFunctionShape | undefined {
        return this.activities
    }

    getNowIdKeyToGuid(): Map<string, string> | undefined {
        return this.nowIdKeyToGuid
    }

    getPlaybookBodyParamsName(): string | undefined {
        return this.playbookBodyParamsName
    }

    getSysId(): NowIdShape | undefined {
        return this.config.get('$id')?.as(NowIdShape)
    }
}

/**
 * Shape class for Activity instances within a Lane's activities arrow.
 * Handles the pattern: Activity(ActivityDefinitions.Core.RecordForm, { config: {...}, inputs: {...}, experienceProperties: {...} })
 *
 * @example
 * Activity(ActivityDefinitions.Core.RecordForm,
 *     config: { $id: Now.ID['act_1'], label: 'Edit record', order: 1, startRule: wfa.playbook.run.Immediately() },
 *     inputs: { assignedTo: wfa.playbook.dataPill(params.inputs.record.assigned_to) },
 *     experienceProperties: { associatedTable: 'incident', associatedRecord: wfa.playbook.dataPill(params.inputs.record) }
 * })
 */
export class ActivityShape extends CallExpressionShape {
    private readonly activityDef: Shape
    private readonly config: ObjectShape
    private readonly inputs: ObjectShape | undefined
    private readonly experienceProperties: ObjectShape | undefined
    private readonly nowIdKeyToGuid: Map<string, string> | undefined
    private readonly playbookBodyParamsNames: string[]

    constructor({
        source,
        activityDefinition,
        config,
        inputs,
        experienceProperties,
        nowIdKeyToGuid,
        playbookBodyParamsNames,
    }: {
        source: Source
        activityDefinition: Shape
        config: ObjectShape
        inputs?: ObjectShape | undefined
        experienceProperties?: ObjectShape | undefined
        nowIdKeyToGuid?: Map<string, string>
        /** Names of arrow function parameters representing PlaybookBodyParams (from lanes + activities levels) */
        playbookBodyParamsNames?: string[]
    }) {
        super({ source, callee: 'ActivityShape', args: [config] })
        this.activityDef = activityDefinition
        this.config = config
        this.inputs = inputs
        this.experienceProperties = experienceProperties
        this.nowIdKeyToGuid = nowIdKeyToGuid
        this.playbookBodyParamsNames = playbookBodyParamsNames ?? []
    }

    /** The activity definition shape (e.g. PropertyAccessShape for ActivityDefinitions.Core.RecordForm) */
    getActivityDefinition(): Shape {
        return this.activityDef
    }

    /** The config object: { $id, label, order, startRule, ... } */
    getConfig(): ObjectShape {
        return this.config
    }

    getInputs(): ObjectShape | undefined {
        return this.inputs
    }

    getExperienceProperties(): ObjectShape | undefined {
        return this.experienceProperties
    }

    getNowIdKeyToGuid(): Map<string, string> | undefined {
        return this.nowIdKeyToGuid
    }

    getPlaybookBodyParamsNames(): string[] {
        return this.playbookBodyParamsNames
    }

    getSysId(): NowIdShape | undefined {
        return this.config.get('$id')?.as(NowIdShape)
    }
}

/**
 * Shape class for trigger instances within PlaybookDefinition declarations.
 * Handles the pattern: wfa.playbook.trigger(triggerType, config, triggerInputs)
 *
 * @example
 * wfa.playbook.trigger(
 *     PlaybookTriggerTypes.RecordCreate,
 *     { $id: Now.ID['trig_1'], label: 'On Create' },
 *     { table: 'incident', condition: 'priority=1' }
 * )
 */
export class TriggerInstanceShape extends CallExpressionShape {
    private triggerType: Shape
    private config: ObjectShape
    private triggerInputs: ObjectShape
    private parentRecordInputSysId: string | undefined
    private mapperMappings: Map<string, MapperValue> | undefined
    private playbookInputSysIds: Map<string, string> | undefined
    private isSameTableAsParent: boolean

    constructor({
        source,
        triggerType,
        config,
        triggerInputs,
        parentRecordInputSysId,
        mapperMappings,
        playbookInputSysIds,
        isSameTableAsParent,
    }: {
        source: Source
        triggerType: Shape
        config: ObjectShape
        triggerInputs: ObjectShape
        parentRecordInputSysId?: string | undefined
        mapperMappings?: Map<string, MapperValue> | undefined
        playbookInputSysIds?: Map<string, string> | undefined
        isSameTableAsParent?: boolean | undefined
    }) {
        super({ source, callee: 'TriggerInstanceShape', args: [triggerType, config, triggerInputs] })
        this.triggerType = triggerType
        this.config = config
        this.triggerInputs = triggerInputs
        this.parentRecordInputSysId = parentRecordInputSysId
        this.mapperMappings = mapperMappings
        this.playbookInputSysIds = playbookInputSysIds
        this.isSameTableAsParent = isSameTableAsParent ?? false
    }

    getTriggerType(): Shape {
        return this.triggerType
    }

    getConfig(): ObjectShape {
        return this.config
    }

    getTriggerInputs(): ObjectShape {
        return this.triggerInputs
    }

    /**
     * Sys_id of the sys_pd_process_input glidevar for `parentRecord`, when the
     * parent playbook has `parentTable` set. Populated by the definition plugin
     * and consumed by the trigger instance plugin to emit the linking
     * sys_variable_value record. Internal wiring — not a user-visible argument.
     */
    getParentRecordInputSysId(): string | undefined {
        return this.parentRecordInputSysId
    }

    /**
     * Mapper values keyed by playbook input element name, parsed from the
     * trigger's playbookInputs mapper callback. Each value contains the
     * serialized string and an isPill flag (true = sys_element_mapping,
     * false = sys_variable_value). Populated by the trigger-array processing
     * transform and consumed by the trigger instance plugin.
     */
    getMapperMappings(): Map<string, MapperValue> | undefined {
        return this.mapperMappings
    }

    /**
     * Map of playbook input element names to their sys_pd_process_input sys_ids.
     * Needed so the trigger instance plugin can create sys_variable_value records
     * for hardcoded mapper values.
     */
    getPlaybookInputSysIds(): Map<string, string> | undefined {
        return this.playbookInputSysIds
    }

    /**
     * True when the playbook has parentTable set and this trigger fires on that
     * same table. Used by the trigger instance plugin to decide whether to emit
     * the default {{triggerRecord}} sys_element_mapping for parent_record.
     */
    getIsSameTableAsParent(): boolean {
        return this.isSameTableAsParent
    }

    getSysId(): NowIdShape | undefined {
        return this.config.get('$id')?.as(NowIdShape)
    }
}
